/* NK - Página de produto: galeria (scroll-snap + miniaturas/indicadores), tamanho, bundle e quantidade.
   A adição à sacola continua com o <product-form> do tema (assets/product-form.js).
   Seletor de variantes, bundle e botão são blocos opcionais: tudo aqui tolera a ausência deles. */
if (!customElements.get('tp-nk-product')) {
  class TpNkProduct extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-nk-track]');
      this.slides = Array.from(this.querySelectorAll('[data-nk-slide]'));
      this.navItems = Array.from(this.querySelectorAll('[data-nk-thumb], [data-nk-dot]'));
      this.variantInput = this.querySelector('[data-nk-variant-input]');
      this.quantityInput = this.querySelector('[data-nk-quantity-input]');
      this.submit = this.querySelector('[data-nk-submit]');
      this.submitLabel = this.querySelector('[data-nk-submit-label]');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.activeIndex = 0;

      const variantsData = this.querySelector('[data-nk-variants]');
      this.variants = variantsData ? JSON.parse(variantsData.textContent) : [];

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onScroll = this.onScroll.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      if (this.track) this.track.addEventListener('scroll', this.onScroll, { passive: true });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
    }

    onClick(event) {
      const navItem = event.target.closest('[data-nk-thumb], [data-nk-dot]');
      if (navItem) {
        this.goTo(Number(navItem.dataset.index));
        return;
      }

      const step = event.target.closest('[data-nk-step]');
      if (step) this.setQuantity(this.currentQuantity() + Number(step.dataset.nkStep));
    }

    onChange(event) {
      const target = event.target;
      if (target.matches('[data-nk-option-input]')) this.updateVariant();
      if (target.matches('[data-nk-offer]') && this.quantityInput) this.quantityInput.value = target.value;
      if (target === this.quantityInput && target.type === 'number') this.setQuantity(this.currentQuantity());
    }

    /* Galeria ------------------------------------------------------------ */
    onScroll() {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        const index = this.visibleIndex();
        if (index !== this.activeIndex) this.setActive(index);
      });
    }

    visibleIndex() {
      if (!this.slides.length) return 0;
      const origin = this.slides[0].offsetLeft;
      const left = this.track.scrollLeft;
      let closest = 0;
      let distance = Infinity;
      this.slides.forEach((slide, index) => {
        const current = Math.abs(slide.offsetLeft - origin - left);
        if (current < distance) {
          distance = current;
          closest = index;
        }
      });
      return closest;
    }

    goTo(index) {
      const slide = this.slides[index];
      if (!slide || !this.track) return;
      this.track.scrollTo({
        left: slide.offsetLeft - this.slides[0].offsetLeft,
        behavior: this.reduceMotion.matches ? 'auto' : 'smooth',
      });
      this.setActive(index);
    }

    setActive(index) {
      this.activeIndex = index;
      this.navItems.forEach((item) => {
        const isActive = Number(item.dataset.index) === index;
        const activeClass = item.hasAttribute('data-nk-thumb') ? 'tp-nk-product__thumb--active' : 'tp-nk-product__dot--active';
        item.classList.toggle(activeClass, isActive);
        if (isActive) {
          item.setAttribute('aria-current', 'true');
        } else {
          item.removeAttribute('aria-current');
        }
      });
    }

    /* Variante ----------------------------------------------------------- */
    updateVariant() {
      const selected = Array.from(this.querySelectorAll('[data-nk-option]')).map((fieldset) => {
        const checked = fieldset.querySelector('[data-nk-option-input]:checked');
        return checked ? checked.value : null;
      });
      const variant = this.variants.find((item) => item.options.every((value, index) => value === selected[index]));

      if (!variant) {
        this.setAvailability(false);
        return;
      }

      if (this.variantInput) this.variantInput.value = variant.id;
      this.querySelectorAll('[data-nk-variant-scope]').forEach((element) => {
        element.hidden = element.dataset.nkVariantScope !== String(variant.id);
      });

      if (variant.media) {
        this.querySelectorAll('[data-nk-packshot]').forEach((element) => {
          element.hidden = element.dataset.nkPackshot !== String(variant.media);
        });
      }
      this.goTo(0);
      this.setAvailability(variant.available);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    setAvailability(available) {
      if (!this.submit || !this.submitLabel) return;
      this.submit.disabled = !available;
      this.submitLabel.textContent = available ? this.submit.dataset.label : this.submit.dataset.labelSoldout;
    }

    /* Quantidade --------------------------------------------------------- */
    currentQuantity() {
      return parseInt(this.quantityInput && this.quantityInput.value, 10) || 1;
    }

    setQuantity(value) {
      if (!this.quantityInput || this.quantityInput.type !== 'number') return;
      const min = Number(this.quantityInput.min) || 1;
      const max = Number(this.quantityInput.max) || 99;
      this.quantityInput.value = Math.min(max, Math.max(min, value));
    }
  }

  customElements.define('tp-nk-product', TpNkProduct);
}
